import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';

type PolicyReader = {
  readonly triggerKey: 'consent:privacyNotice.readFull' | 'consent:termsAndConditions.readFull';
  readonly titleKey: 'consent:privacyNotice.title' | 'consent:termsAndConditions.title';
  readonly summaryKey: 'consent:privacyNotice.loginSummary' | 'consent:termsAndConditions.summary';
  readonly bodyKey: 'consent:privacyNotice.body' | 'consent:termsAndConditions.body';
};

const READERS: readonly PolicyReader[] = [
  {
    triggerKey: 'consent:privacyNotice.readFull',
    titleKey: 'consent:privacyNotice.title',
    summaryKey: 'consent:privacyNotice.loginSummary',
    bodyKey: 'consent:privacyNotice.body',
  },
  {
    triggerKey: 'consent:termsAndConditions.readFull',
    titleKey: 'consent:termsAndConditions.title',
    summaryKey: 'consent:termsAndConditions.summary',
    bodyKey: 'consent:termsAndConditions.body',
  },
];

/**
 * Opens one policy's full text in a side sheet — the read affordance the login summary previously
 * lacked (point 1): the summary invited the reader to "review the notice" with no way to actually
 * open it. Read-only, no accept action and no API call, so Resolution 7's kill switch still does
 * not apply here — the blocking acceptance flow remains `AcceptanceGate`/`PolicyAcceptanceScreen`.
 *
 * `trigger` replaces the default link-styled button when the sheet is opened from somewhere else —
 * the shell's footer — so the notice is written once and read from both places.
 */
function PolicyReaderSheet({
  triggerKey,
  titleKey,
  summaryKey,
  bodyKey,
  trigger,
}: PolicyReader & { readonly trigger?: ReactElement }) {
  const { t } = useTranslation(['consent']);
  const paragraphs = t(bodyKey, { returnObjects: true });

  return (
    <Sheet>
      <SheetTrigger asChild>
        {trigger ?? (
          // `text-link`, not `text-primary`: the action colour belongs to the one primary action
          // per view (Brand Kit 1.0.0), and a link to a document is not that action.
          <Button
            variant="ghost"
            size="sm"
            className="text-link h-auto p-0 font-semibold underline-offset-4 hover:bg-transparent
              hover:underline"
          >
            {t(triggerKey)}
          </Button>
        )}
      </SheetTrigger>
      <SheetContent side="right" className="w-full gap-0 overflow-y-auto p-6 sm:max-w-lg">
        <div className="space-y-1.5">
          <SheetTitle className="text-lg">{t(titleKey)}</SheetTitle>
          <SheetDescription>{t(summaryKey)}</SheetDescription>
        </div>
        <div className="text-foreground/80 mt-5 space-y-3 text-sm leading-relaxed">
          {paragraphs.map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

/** The privacy notice's full text, opened by a caller-supplied trigger (the shell's footer). */
export function PrivacyNoticeSheet({ trigger }: { readonly trigger: ReactElement }) {
  return <PolicyReaderSheet {...READERS[0]!} trigger={trigger} />;
}

/**
 * Static, read-only privacy/T&C surface shown at login, pre-authentication (Resolution 9): the
 * one-line summary plus links that open each document's full text. Never blocks anything.
 */
export function PrivacyNoticeSummary() {
  const { t } = useTranslation(['consent']);

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>{t('consent:privacyNotice.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-foreground/80 text-sm">{t('consent:privacyNotice.loginSummary')}</p>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          {READERS.map((reader) => (
            <PolicyReaderSheet key={reader.triggerKey} {...reader} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
